// OpenRouter API Service
// This service handles all communication with OpenRouter API

import { getOpenRouterConfig, validateOpenRouterConfig, getModelName, fetchOpenRouterConfig } from '../config/openrouter';

export interface OpenRouterResponse {
  choices: {
    message: {
      content: string;
      role: string;
    };
    finish_reason: string;
  }[];
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  model: string;
  id: string;
}

export interface ScoringResult {
  success: boolean;
  content?: string;
  error?: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    model: string;
    model_name: string;
  };
  response_time?: number;
  scoreSaved?: boolean;
  savedScore?: number;
}

/**
 * Call OpenRouter API to score a Reddit post
 */
export const scoreRedditPostWithLLM = async (
  prompt: string, 
  selectedModel?: string
): Promise<ScoringResult> => {
  const startTime = Date.now();
  
  try {
    // Fetch configuration from backend first
    await fetchOpenRouterConfig();
    
    // Get and validate configuration
    const config = getOpenRouterConfig();
    if (!validateOpenRouterConfig(config)) {
      return {
        success: false,
        error: "OpenRouter API configuration is invalid. Please configure the API key in the Django admin panel under LLM Providers."
      };
    }

    // Use selected model or default
    const modelId = selectedModel || config.defaultModel;
    
    // Prepare request headers
    const headers = {
      "Authorization": `Bearer ${config.apiKey}`,
      "Content-Type": "application/json",
      "HTTP-Referer": window.location.origin,
      "X-Title": "Gudu Portal - Reddit Post Scoring"
    };

    // Prepare request payload
    const payload = {
      model: modelId,
      messages: [
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: config.temperature,
      max_tokens: config.maxTokens,
      stream: false
    };

    console.log(`🤖 Calling OpenRouter API with model: ${getModelName(modelId)}`);

    // Make the API call
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    try {
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed with status ${response.status}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error && errorJson.error.message) {
            errorMessage = errorJson.error.message;
          }
        } catch {
          // If we can't parse the error as JSON, use the raw text
          if (errorText) {
            errorMessage = errorText;
          }
        }
        
        return {
          success: false,
          error: errorMessage,
          response_time: Date.now() - startTime
        };
      }

      // Parse response
      const result: OpenRouterResponse = await response.json();
      
      // Extract content from response
      const content = extractContentFromResponse(result);
      if (!content) {
        return {
          success: false,
          error: "Failed to extract content from API response",
          response_time: Date.now() - startTime
        };
      }

      // Return successful result
      return {
        success: true,
        content,
        usage: {
          prompt_tokens: result.usage?.prompt_tokens || 0,
          completion_tokens: result.usage?.completion_tokens || 0,
          total_tokens: result.usage?.total_tokens || 0,
          model: modelId,
          model_name: getModelName(modelId)
        },
        response_time: Date.now() - startTime
      };

    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        return {
          success: false,
          error: `Request timed out after ${config.timeout / 1000} seconds`,
          response_time: Date.now() - startTime
        };
      }
      
      throw fetchError;
    }

  } catch (error: any) {
    console.error('OpenRouter API Error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred while calling the API',
      response_time: Date.now() - startTime
    };
  }
};

/**
 * Extract content from different response formats
 */
const extractContentFromResponse = (response: OpenRouterResponse): string | null => {
  try {
    // Standard OpenAI/Claude format
    if (response.choices && response.choices.length > 0) {
      const choice = response.choices[0];
      if (choice.message && choice.message.content) {
        return choice.message.content;
      }
    }

    console.error('Unknown response format:', response);
    return null;
  } catch (error) {
    console.error('Error extracting content from response:', error);
    return null;
  }
};

/**
 * Test OpenRouter API connection
 */
export const testOpenRouterConnection = async (): Promise<ScoringResult> => {
  const testPrompt = "Please respond with: 'OpenRouter API connection successful!'";
  return await scoreRedditPostWithLLM(testPrompt);
};

/**
 * Initialize OpenRouter configuration from backend
 * Call this when the app starts up
 */
export const initializeOpenRouterConfig = async (): Promise<void> => {
  try {
    await fetchOpenRouterConfig();
    console.log('🔧 OpenRouter configuration initialized');
  } catch (error) {
    console.warn('⚠️ Failed to initialize OpenRouter config:', error);
  }
};